/**
 * Logger utility with environment variable control
 * 
 * Environment Variables (set at runtime via Docker/Kubernetes):
 * - VUE_APP_LOG_LEVEL: 'debug' | 'info' | 'warn' | 'error' | 'none' (default: 'warn' in production, 'debug' in development)
 * - VUE_APP_ENABLE_LOGS: 'true' | 'false' (default: 'true' in development, 'false' in production)
 * 
 * Note: These values are replaced at runtime by substitute_environment_variables.sh
 * Placeholders are used during build and replaced when container starts
 */

const LOG_LEVELS = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
  NONE: 4
}

// Placeholder constants
const PLACEHOLDERS = {
  ENABLE_LOGS: 'true',
  LOG_LEVEL: 'warn'
}

// Cache for log level (invalidated every 5 seconds)
let cachedLogLevel = null
let cacheTimestamp = 0
const CACHE_TTL_MS = 5000

/**
 * Check if logging is enabled
 * @returns {boolean}
 */
function isLoggingEnabled() {
  const enableLogs = process.env.VUE_APP_ENABLE_LOGS || PLACEHOLDERS.ENABLE_LOGS
  
  // Fast path: explicit false
  if (enableLogs === 'false') {
    return false
  }
  
  // Fast path: explicit true
  if (enableLogs === 'true') {
    return true
  }
  
  // Placeholder case: use default based on NODE_ENV
  if (enableLogs === PLACEHOLDERS.ENABLE_LOGS) {
    return process.env.NODE_ENV !== 'production'
  }
  
  // Fallback: treat as enabled
  return true
}

/**
 * Get log level string from environment or default
 * @returns {string} Log level string
 */
function getLogLevelFromEnv() {
  // In development, process.env.VUE_APP_LOG_LEVEL might be set directly
  // In production, placeholder will be replaced by substitute_environment_variables.sh
  let envLogLevel = process.env.VUE_APP_LOG_LEVEL
  
  // Debug in development
  if (process.env.NODE_ENV !== 'production') {
    console.debug('[Logger] process.env.VUE_APP_LOG_LEVEL:', envLogLevel, 'type:', typeof envLogLevel)
  }
  
  // If not set, use placeholder (will be replaced in production)
  if (!envLogLevel) {
    envLogLevel = PLACEHOLDERS.LOG_LEVEL
  }
  
  // If placeholder not replaced (development mode), use default
  if (envLogLevel === PLACEHOLDERS.LOG_LEVEL) {
    const defaultLevel = process.env.NODE_ENV === 'production' ? 'warn' : 'debug'
    if (process.env.NODE_ENV !== 'production') {
      console.debug('[Logger] Placeholder not replaced, using default:', defaultLevel)
    }
    return defaultLevel
  }
  
  // Ensure we return a string
  if (typeof envLogLevel !== 'string') {
    const defaultLevel = process.env.NODE_ENV === 'production' ? 'warn' : 'debug'
    if (process.env.NODE_ENV !== 'production') {
      console.warn('[Logger] envLogLevel is not a string:', envLogLevel, 'type:', typeof envLogLevel, 'using default:', defaultLevel)
    }
    return defaultLevel
  }
  
  const normalized = envLogLevel.toLowerCase().trim()
  
  // Return normalized string (will be validated in parseLogLevel)
  return normalized || (process.env.NODE_ENV === 'production' ? 'warn' : 'debug')
}

/**
 * Convert log level string to number
 * @param {string} levelStr - Log level string
 * @returns {number} Log level number
 */
function parseLogLevel(levelStr) {
  // Validate input
  if (!levelStr || typeof levelStr !== 'string') {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(`[Logger] Invalid log level input: "${levelStr}" (type: ${typeof levelStr}). Using default: warn`)
    }
    return LOG_LEVELS.WARN
  }
  
  const levelMap = {
    debug: LOG_LEVELS.DEBUG,
    info: LOG_LEVELS.INFO,
    warn: LOG_LEVELS.WARN,
    error: LOG_LEVELS.ERROR,
    none: LOG_LEVELS.NONE
  }
  
  // Ensure levelStr is a string before calling toLowerCase
  const normalized = String(levelStr).toLowerCase().trim()
  
  // Check if normalized string is empty
  if (!normalized || normalized.length === 0) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(`[Logger] Empty log level string after normalization. Input: "${levelStr}". Using default: warn`)
    }
    return LOG_LEVELS.WARN
  }
  
  // Direct lookup in levelMap
  // Use 'in' operator to check if key exists (works even if value is 0/falsy)
  if (!(normalized in levelMap)) {
    // Only warn in development to avoid console spam in production
    if (process.env.NODE_ENV !== 'production') {
      console.warn(`[Logger] Invalid log level: "${levelStr}" (normalized: "${normalized}", type: ${typeof normalized}). Valid levels: ${Object.keys(levelMap).join(', ')}. Using default: warn`)
      console.warn(`[Logger] Debug - levelMap has key "${normalized}":`, normalized in levelMap)
      console.warn(`[Logger] Debug - levelMap:`, levelMap)
    }
    return LOG_LEVELS.WARN
  }
  
  return levelMap[normalized]
}

/**
 * Get current log level from environment or default
 * @returns {number} Log level number
 */
function getLogLevel() {
  if (!isLoggingEnabled()) {
    return LOG_LEVELS.NONE
  }
  
  const levelStr = getLogLevelFromEnv()
  const parsedLevel = parseLogLevel(levelStr)
  
  // Debug logging in development
  if (process.env.NODE_ENV !== 'production') {
    console.debug('[Logger] getLogLevelFromEnv returned:', levelStr, 'type:', typeof levelStr, 'parsed:', parsedLevel)
  }
  
  return parsedLevel
}

/**
 * Get current log level with caching
 * Re-evaluates every CACHE_TTL_MS milliseconds
 * @returns {number} Log level number
 */
function getCurrentLogLevel() {
  const now = Date.now()
  if (!cachedLogLevel || (now - cacheTimestamp) > CACHE_TTL_MS) {
    cachedLogLevel = getLogLevel()
    cacheTimestamp = now
  }
  return cachedLogLevel
}

/**
 * Logger class with level-based logging
 */
class Logger {
  constructor(context = '') {
    this.context = context
  }

  /**
   * Create a logger instance with context
   * @param {string} context - Context prefix for logs
   * @returns {Logger}
   */
  static create(context) {
    return new Logger(context)
  }


  /**
   * Format log message with context (only called when actually logging)
   * @param {string} level - Log level
   * @param {...any} args - Log arguments
   * @returns {Array} Formatted log arguments
   * @private
   */
  _formatMessage(level, ...args) {
    if (this.context) {
      return [`[${this.context}]`, `[${level.toUpperCase()}]`, ...args]
    }
    return [`[${level.toUpperCase()}]`, ...args]
  }

  /**
   * Debug log
   * @param {...any} args - Log arguments
   */
  debug(...args) {
    if (getCurrentLogLevel() <= LOG_LEVELS.DEBUG) {
      console.debug(...this._formatMessage('debug', ...args))
    }
  }

  /**
   * Info log
   * @param {...any} args - Log arguments
   */
  info(...args) {
    if (getCurrentLogLevel() <= LOG_LEVELS.INFO) {
      console.info(...this._formatMessage('info', ...args))
    }
  }

  /**
   * Warning log
   * @param {...any} args - Log arguments
   */
  warn(...args) {
    if (getCurrentLogLevel() <= LOG_LEVELS.WARN) {
      console.warn(...this._formatMessage('warn', ...args))
    }
  }

  /**
   * Error log
   * @param {...any} args - Log arguments
   */
  error(...args) {
    if (getCurrentLogLevel() <= LOG_LEVELS.ERROR) {
      console.error(...this._formatMessage('error', ...args))
    }
  }

  /**
   * Log (alias for info)
   * @param {...any} args - Log arguments
   */
  log(...args) {
    this.info(...args)
  }
}

// Create default logger instance
const logger = new Logger()

// Export default logger and Logger class
export default logger
export { Logger, LOG_LEVELS }

// Convenience functions for backward compatibility
/**
 * Convenience function for debug logging
 * @param {...any} args - Log arguments
 */
export const debug = (...args) => logger.debug(...args)

/**
 * Convenience function for info logging
 * @param {...any} args - Log arguments
 */
export const info = (...args) => logger.info(...args)

/**
 * Convenience function for warning logging
 * @param {...any} args - Log arguments
 */
export const warn = (...args) => logger.warn(...args)

/**
 * Convenience function for error logging
 * @param {...any} args - Log arguments
 */
export const error = (...args) => logger.error(...args)

/**
 * Convenience function for log (info) logging
 * @param {...any} args - Log arguments
 */
export const log = (...args) => logger.log(...args)
